'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const segmentedControlVariants = cva(
  'inline-flex items-center rounded-md bg-muted text-muted-foreground',
  {
    variants: {
      size: {
        default: 'h-10 gap-1 p-1',
        thin: 'h-8 gap-0.5 p-0.5',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

const segmentVariants = cva(
  [
    'inline-flex min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-sm text-sm font-medium',
    'ring-offset-background transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
    'disabled:pointer-events-none disabled:opacity-50',
    // Scoped to the unselected segments: an unscoped hover would tie with the
    // selected colour on specificity, and Tailwind — not the class order here —
    // would decide which one wins.
    'data-[state=off]:hover:text-foreground',
    // `primary` reads at 6:1 on the light pill but only 3.3:1 on the near-black
    // dark background, under the 4.5:1 AA floor — `primary-300` is the same hue
    // inverted for dark, and the `dark:` prefix outranks the base colour on
    // specificity, so the order of these two classes does not decide it.
    'data-[state=on]:bg-background data-[state=on]:shadow-sm',
    'data-[state=on]:text-primary dark:data-[state=on]:text-primary-300',
  ],
  {
    variants: {
      size: {
        default: 'h-8 px-3',
        thin: 'h-7 px-2.5',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

const NEXT_KEYS = new Set(['ArrowRight', 'ArrowDown']);
const PREVIOUS_KEYS = new Set(['ArrowLeft', 'ArrowUp']);

type SegmentedControlOption<TValue extends string> = {
  value: TValue;
  label: React.ReactNode;
  disabled?: boolean;
};

type SegmentedControlProps<TValue extends string> = {
  options: SegmentedControlOption<TValue>[];
  value: TValue;
  onValueChange: (value: TValue) => void;
  /** Segments share the full width instead of hugging their labels. */
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
} & VariantProps<typeof segmentedControlVariants>;

/**
 * A one-of-N switch: the options are stated side by side instead of hidden
 * behind a `Select`, which is what makes it worth its width — two or three
 * mutually exclusive choices are read faster than they are opened.
 *
 * Built on the radiogroup pattern rather than on `Tabs`, which looks the part
 * but promises a tab panel underneath: here the choice reconfigures the form
 * around it, so `role="radio"` is what a screen reader has to hear. Arrow keys
 * move the selection and skip disabled segments, and only the selected segment
 * is in the tab order — a radiogroup is one stop, not N.
 *
 * The key handler sits on the segments rather than on the group: the segment is
 * what holds the focus, so it is where the press actually lands, and the group
 * stays a plain container instead of a second focusable element beside them.
 */
const SegmentedControl = <TValue extends string>({
  options,
  value,
  onValueChange,
  fullWidth,
  disabled,
  size,
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: SegmentedControlProps<TValue>) => {
  const segmentRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

  const selectableIndexes = options.reduce<number[]>(
    (indexes, option, index) =>
      option.disabled === true ? indexes : [...indexes, index],
    [],
  );

  const selectedIndex = options.findIndex((option) => option.value === value);

  // The selected segment holds the group's single tab stop. When `value` matches
  // nothing — a definition loaded with a value this control does not offer — the
  // first selectable segment holds it, so the group stays reachable.
  const tabStopIndex =
    selectedIndex === -1 ? selectableIndexes[0] ?? -1 : selectedIndex;

  const select = (index: number) => {
    const option = options[index];
    if (option === undefined) {
      return;
    }
    segmentRefs.current[index]?.focus();
    if (option.value !== value) {
      onValueChange(option.value);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled === true || selectableIndexes.length === 0) {
      return;
    }
    const position = selectableIndexes.indexOf(tabStopIndex);
    const lastPosition = selectableIndexes.length - 1;

    const nextPosition = (): number | undefined => {
      if (NEXT_KEYS.has(event.key)) {
        return position === lastPosition ? 0 : position + 1;
      }
      if (PREVIOUS_KEYS.has(event.key)) {
        return position <= 0 ? lastPosition : position - 1;
      }
      if (event.key === 'Home') {
        return 0;
      }
      if (event.key === 'End') {
        return lastPosition;
      }
      return undefined;
    };

    const target = nextPosition();
    if (target === undefined) {
      return;
    }
    // Arrow keys own the selection inside a radiogroup; letting the page scroll
    // as well would move the viewport away from the control being operated.
    event.preventDefault();
    select(selectableIndexes[target]);
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={cn(
        segmentedControlVariants({ size }),
        fullWidth === true && 'flex w-full',
        className,
      )}
    >
      {options.map((option, index) => {
        const isSelected = option.value === value;
        return (
          <button
            key={option.value}
            ref={(element) => {
              segmentRefs.current[index] = element;
            }}
            type="button"
            role="radio"
            aria-checked={isSelected}
            data-state={isSelected ? 'on' : 'off'}
            tabIndex={index === tabStopIndex ? 0 : -1}
            disabled={disabled === true || option.disabled === true}
            onClick={() => select(index)}
            onKeyDown={handleKeyDown}
            className={cn(
              segmentVariants({ size }),
              fullWidth === true && 'flex-1',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export { SegmentedControl, segmentedControlVariants, segmentVariants };
export type { SegmentedControlOption, SegmentedControlProps };
